import {
  MAXIMUM_FUEL_SURCHARGE_PERCENTAGE,
  NetworkRateCardZone,
  PALLET_UNIT,
  PERCENT_OF_CARRIAGE,
  QuantityBreak,
  SERVICE_LEVELS,
  SURCHARGE_UNITS,
  ServiceIdentity,
  Surcharge,
  ZoneLocation,
  ZoneServicePricing,
  getServiceLabel,
  getUniqueServices,
  isFuelSurcharge,
  isKnownPalletType,
  isPricedSurcharge,
  isServiceLevel,
} from './types'

export interface RateCardBeingEdited {
  networkName: string
  zones: NetworkRateCardZone[]
  surcharges: Surcharge[]
}

const MAXIMUM_EXAMPLES_IN_A_MESSAGE = 5

function listExamples(examples: string[]): string {
  const listedExamples = examples.slice(0, MAXIMUM_EXAMPLES_IN_A_MESSAGE).join(', ')
  if (examples.length <= MAXIMUM_EXAMPLES_IN_A_MESSAGE) return listedExamples
  return `${listedExamples} and ${examples.length - MAXIMUM_EXAMPLES_IN_A_MESSAGE} more`
}

function describeServiceLevelProblem(service: ServiceIdentity): string[] {
  if (isServiceLevel(service.serviceName)) return []
  return [
    `"${service.serviceName}" is not a recognised service level. Open each column that uses it and pick one of: ${SERVICE_LEVELS.join(', ')}.`,
  ]
}

function describePalletTypeProblem(service: ServiceIdentity): string[] {
  if (service.unit !== PALLET_UNIT || isKnownPalletType(service.palletType)) return []
  if (!service.palletType) return [`"${getServiceLabel(service)}" needs a pallet type. Open its column and pick one.`]
  return [`"${service.palletType}" is not a supported pallet type. Open each column that uses it and pick one.`]
}

export function listServiceColumnProblems(services: ServiceIdentity[]): string[] {
  const problems = services.flatMap((service) => [
    ...describeServiceLevelProblem(service),
    ...describePalletTypeProblem(service),
  ])
  return Array.from(new Set(problems))
}

function describeMissingNetworkName(networkName: string): string | null {
  if (networkName.trim()) return null
  return 'Network name is required'
}

function describeZoneWithoutName(zones: NetworkRateCardZone[]): string | null {
  const isAnyZoneUnnamed = zones.some((zone) => !zone.zoneName.trim())
  if (!isAnyZoneUnnamed) return null
  return 'Every zone needs a name. Rename the zone with no name before saving.'
}

function describeDuplicateZoneNames(zones: NetworkRateCardZone[]): string | null {
  const zoneCountByName = new Map<string, number>()
  zones.forEach((zone) => {
    const name = zone.zoneName.toLowerCase().trim()
    zoneCountByName.set(name, (zoneCountByName.get(name) ?? 0) + 1)
  })
  const duplicateNames = Array.from(zoneCountByName.entries())
    .filter(([, count]) => count > 1)
    .map(([name]) => name)
  if (duplicateNames.length === 0) return null
  return `Zone names must be unique. Duplicates: ${duplicateNames.join(', ')}`
}

function describeZonesWithoutPostcodes(zones: NetworkRateCardZone[]): string | null {
  const zonesWithoutPostcodes = zones.filter((zone) => zone.locations.length === 0)
  if (zonesWithoutPostcodes.length === 0) return null
  const names = zonesWithoutPostcodes.map((zone) => zone.zoneName || 'Unnamed').join(', ')
  return `Every zone needs at least one postcode. Missing: ${names}`
}

function isUnresolved(location: ZoneLocation): boolean {
  return !location.geoPoint || (location.geoPoint.lat === 0 && location.geoPoint.lon === 0)
}

function describeUnresolvedPostcodes(zones: NetworkRateCardZone[]): string | null {
  const unresolvedPostcodes = zones.flatMap((zone) =>
    zone.locations
      .filter(isUnresolved)
      .map((location) => `"${location.prefix}" in "${zone.zoneName || 'Unnamed'}"`)
  )
  if (unresolvedPostcodes.length === 0) return null
  return `Cannot save: ${unresolvedPostcodes.length} postcode${unresolvedPostcodes.length === 1 ? ' is' : 's are'} unresolved (${listExamples(unresolvedPostcodes)}). Open the affected zones to retry or remove them.`
}

function describeRepeatedPostcode(zones: NetworkRateCardZone[]): string | null {
  const firstZoneNameByPostcode = new Map<string, string>()
  const postcodesInZoneOrder = zones.flatMap((zone) =>
    zone.locations.map((location) => ({ prefix: location.prefix, zoneName: zone.zoneName }))
  )
  const repeatedPostcode = postcodesInZoneOrder.find(({ prefix, zoneName }) => {
    if (firstZoneNameByPostcode.has(prefix)) return true
    firstZoneNameByPostcode.set(prefix, zoneName)
    return false
  })
  if (!repeatedPostcode) return null
  const firstZoneName = firstZoneNameByPostcode.get(repeatedPostcode.prefix)
  return `Postcode "${repeatedPostcode.prefix}" exists in both "${firstZoneName}" and "${repeatedPostcode.zoneName}". Postcodes must be unique across zones.`
}

function isPriceMissing(quantityBreak: QuantityBreak): boolean {
  return quantityBreak.price === null || quantityBreak.price === undefined || Number.isNaN(quantityBreak.price)
}

function describeMissingPrice(zones: NetworkRateCardZone[]): string | null {
  const servicesInZoneOrder = zones.flatMap((zone) => zone.servicePricing.map((pricing) => ({ zone, pricing })))
  const serviceWithoutPrice = servicesInZoneOrder.find(({ pricing }) => pricing.breaks.some(isPriceMissing))
  if (!serviceWithoutPrice) return null
  return `Price is required for "${getServiceLabel(serviceWithoutPrice.pricing)}" in zone "${serviceWithoutPrice.zone.zoneName}". It can be 0 but cannot be empty.`
}

function describeMissingZones(zones: NetworkRateCardZone[]): string | null {
  if (zones.length > 0) return null
  return 'At least one zone is required'
}

function describeMissingServices(zones: NetworkRateCardZone[]): string | null {
  if (getUniqueServices(zones).length > 0) return null
  return 'Add at least one service before saving.'
}

function describeSurchargesToDelete(surcharges: Surcharge[]): string | null {
  const surchargesToDelete = surcharges.filter((surcharge) => !surcharge.selectableSurchargeType)
  if (surchargesToDelete.length === 0) return null
  const names = surchargesToDelete.map((surcharge) => `"${surcharge.name || 'Unnamed surcharge'}"`).join(', ')
  return `Delete the surcharges FLOX can't price before saving: ${names}.`
}

function hasAUnitItsTypeAllows(surcharge: Surcharge): boolean {
  if (isFuelSurcharge(surcharge)) return surcharge.unit === PERCENT_OF_CARRIAGE
  return SURCHARGE_UNITS.some((unit) => unit.value === surcharge.unit)
}

function describeSurchargesWithoutSupportedUnit(surcharges: Surcharge[]): string | null {
  const surchargesWithoutSupportedUnit = surcharges
    .filter(isPricedSurcharge)
    .filter((surcharge) => surcharge.selectableSurchargeType && !hasAUnitItsTypeAllows(surcharge))
  if (surchargesWithoutSupportedUnit.length === 0) return null
  const types = surchargesWithoutSupportedUnit.map((surcharge) => `"${surcharge.selectableSurchargeType}"`).join(', ')
  return `Every surcharge with a price needs a unit. ${types} ${surchargesWithoutSupportedUnit.length === 1 ? 'has none or an unsupported one' : 'have none or an unsupported one'} — pick per consignment or per pallet.`
}

function describeFuelSurchargeOverTheMaximum(surcharges: Surcharge[]): string | null {
  const isFuelSurchargeOverTheMaximum = surcharges
    .filter(isPricedSurcharge)
    .some((surcharge) => isFuelSurcharge(surcharge) && surcharge.price > MAXIMUM_FUEL_SURCHARGE_PERCENTAGE)
  if (!isFuelSurchargeOverTheMaximum) return null
  return `Fuel surcharge cannot be more than ${MAXIMUM_FUEL_SURCHARGE_PERCENTAGE}%.`
}

function describeNegativeSurcharges(surcharges: Surcharge[]): string | null {
  const negativeSurcharges = surcharges.filter(isPricedSurcharge).filter((surcharge) => surcharge.price < 0)
  if (negativeSurcharges.length === 0) return null
  const names = negativeSurcharges.map((surcharge) => `"${surcharge.name}"`).join(', ')
  return `Surcharge price cannot be negative: ${names}.`
}

function isPriceUnset(pricing: ZoneServicePricing): boolean {
  return (pricing.breaks[0]?.price ?? 0) <= 0
}

function describeUnsetPrices(zones: NetworkRateCardZone[]): string | null {
  const unsetPrices = zones.flatMap((zone) =>
    zone.servicePricing
      .filter(isPriceUnset)
      .map((pricing) => `"${getServiceLabel(pricing)}" in "${zone.zoneName || 'Unnamed'}"`)
  )
  if (unsetPrices.length === 0) return null
  return `${unsetPrices.length} price${unsetPrices.length === 1 ? ' is' : 's are'} not set yet (${listExamples(unsetPrices)}). Open the cells showing Set price.`
}

function isProblem(problem: string | null): problem is string {
  return problem !== null
}

export function listProblemsBlockingSave({ networkName, zones, surcharges }: RateCardBeingEdited): string[] {
  return [
    describeMissingNetworkName(networkName),
    describeZoneWithoutName(zones),
    describeDuplicateZoneNames(zones),
    describeZonesWithoutPostcodes(zones),
    describeUnresolvedPostcodes(zones),
    describeRepeatedPostcode(zones),
    describeMissingPrice(zones),
    ...listServiceColumnProblems(getUniqueServices(zones)),
    describeMissingZones(zones),
    describeMissingServices(zones),
    describeSurchargesToDelete(surcharges),
    describeSurchargesWithoutSupportedUnit(surcharges),
    describeNegativeSurcharges(surcharges),
    describeFuelSurchargeOverTheMaximum(surcharges),
  ].filter(isProblem)
}

export function findProblemBlockingSave(card: RateCardBeingEdited): string | null {
  return listProblemsBlockingSave(card)[0] ?? null
}

export function listProblemsToFixAfterImport(card: RateCardBeingEdited): string[] {
  return [...listProblemsBlockingSave(card), describeUnsetPrices(card.zones)].filter(isProblem)
}
